<template>
  <b-modal
    id="select-image-to-analyse-modal"
    ref="selectLayoutModal"
    class="select-images-modal"
    title="Select images to analyze"
    size="lg"
    :ok-disabled="!selectedItems.length"
    @shown="getTemplates"
    @hidden="onModalHidden"
  >
    <template #modal-title>
      <span class="select-images-modal__title">Select images to analyze</span>
    </template>
    <small
      v-if="isOptixMode && previews.length > 0"
    >
      To update thumbnails, open the
      <a
        :href="`/banner/${customerId}/group/${layoutId}/show`"
        class="btn-save-changes"
        target="_blank"
      >
        layout
      </a>
      and navigate through each page. (new tab)
    </small>
    <div class="select-images-modal__actions">
      <b-form-checkbox
        :checked="isAllSelected"
        @change="toggleSelectAll"
      >
        {{ isAllSelected ? 'Unselect all' : 'Select all' }}
      </b-form-checkbox>
    </div>
    <div 
      v-if="!isLoading && previews.length > 0"
      class="mt-2 image-grid"
    >
      <div 
        v-for="(preview, index) in previews" 
        :key="index"
        class="image-card-template"
        :class="{ selected: selectedItems.includes(preview.id) }"
        @click="toggleSelect(preview)"
      >
        <img
          v-if="preview.thumbnail"
          :src="`/share?file=${preview.thumbnail}`"
          :alt="`generated_image_${index + 1}`"
          :style="{ objectFit: isOptixMode ? 'contain' : 'cover' }"
          @load="onImageLoad"
        > 
        <a
          v-else-if="isOptixMode && !preview.thumbnail"
          :href="`/banner/${customerId}/group/${layoutId}/show`"
          class="btn-save-changes"
          target="_blank"
        >
          Update thumbnail
        </a>
        <div
          v-if="selectedItems.includes(preview.id)"
          class="image-card-template__check"
        >
          <i class="bi-check" />
        </div>
      </div>
    </div>
    <div
      v-else-if="isLoading"
      class="d-flex justify-content-center align-items-center flex-column"
    >
      <b-spinner
        class="mr-1 thumbnail-loading"
        variant="light"
      />
      <span>Loading previews...</span>
    </div>
    <div 
      v-else
      class="d-flex justify-content-center align-items-center"
      style="height: 200px;"
    >
      <span>No templates available</span>
    </div>
    <template #modal-footer>
      <div
        class="d-flex justify-content-between align-items-center w-100"
      >
        <span class="select-images-modal__selected">{{ selected }} Selected</span>
        <div class="buttons">
          <b-button
            v-if="isOptixMode"
            class="mr-4"
            variant="outline-secondary"
            @click="getTemplates"
          >
            Reload
          </b-button>
          <b-button 
            variant="light" 
            @click="$bvModal.hide('select-image-to-analyse-modal')"
          >
            Cancel
          </b-button>
          <b-button 
            class="select-images-modal__submit-btn"
            variant="primary" 
            :disabled="!selectedItems.length" 
            @click="handleOkClick"
          >
            {{ !isOptixMode ? 'Continue on Optix Lab' : 'Select' }}
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>
<script>

import LayoutAPI from "@frontend/services/api/layout";
import {ShowPageScopeCollector} from '@frontend/group/modules/scope-collectors/show-page-collector';

const getShowPageScope = () => ShowPageScopeCollector.get([
    'saveLayout',
    'saveShareProof',
    'layoutChanges'
]);

export default {
    props: {
    mode: {
      type: String,
      default: 'default'
    }
  },
  data() {
    return {
      selectedItems: [],
      presetTemplates: [],
      previews: [],
      selected: 0,
      isLoading: true,
      loadedCount: 0,
      isOptixMode: false,
      layoutId: null,
      customerId: null,
    }
  },
  computed: {
    isAllSelected() {
      return this.previews.length && this.selectedItems.length === this.previews.length;
    }
  },
  watch: {
    selectedItems() {
      this.selected = this.selectedItems.length;
    }
  },
  mounted() {
    window.addEventListener("select-image-to-analyse-modal", () => {
      this.$bvModal.show("select-image-to-analyse-modal");
    });
  },
  methods: {
    show(layoutId, customerId) {
      this.$refs.selectLayoutModal.show();
      this.layoutId = layoutId;
      this.customerId = customerId;
      this.isOptixMode = true;
    },
    async handleOkClick() {
      if (this.isOptixMode) {
        const urls = this.presetTemplates.map(t => '/share?file=' + t.thumbnail);

        const files = [];
        for (const url of urls) {
          const filename = url.split('/').pop();
          const file = await this.urlToFile(url, filename);
          files.push({
            originalFile: file,
            url
          });
        }
        this.$emit('onSelectedItems', {images: files});
      } else {
        localStorage.setItem('optix-lab-presets', JSON.stringify(this.presetTemplates))
        this.$bvModal.hide('select-image-to-analyse-modal');
        window.location.href = '/optix#optix-lab'; 
      }
    },
    async urlToFile(url, filename, mimeType) {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: mimeType || blob.type });
    },
    toggleSelect(preview) {
        if (this.selectedItems.includes(preview.id)) {
            this.selectedItems = this.selectedItems.filter(item => item !== preview.id);
            this.presetTemplates = this.presetTemplates.filter(preview => preview.id !== preview.id);
        } else {
            this.selectedItems.push(preview.id);
            
            const template = this.mapTemplateToPreset(preview);
            
            this.presetTemplates.push(template);
        }
    },
    async getTemplates() {
      this.isLoading = true;
      const hasChanges = !$('.btn-save-changes').is(':disabled');
      const grid = $("#template-group-preview");
      const layoutId = grid.attr("data-layout-id") || this.layoutId;
      const customer_id = grid.attr("data-customer-id") || this.customerId;
      if (hasChanges && !this.isOptixMode) {
        const batch_id = await this.updateThumbnails(customer_id, layoutId);
        if (batch_id) {
          // Polling to check if thumbnails are ready
          let ready = false;
          while (!ready) {
            const { data } = await axios.post(`/check-thumbnails-ready`, { batch_id });

            ready = data.ready;
            if (!ready) await new Promise(r => setTimeout(r, 1000));
          }
        }
      }
      
      let { data } = await LayoutAPI.getPreviews(0, layoutId);
      this.previews = [];
      if (data?.instances) {
        const map = new Map(data?.instances.map(instance => [instance.instance_id, instance]))
        const sorted = data?.settings?.map(o => map.get(o.instance_id)).filter(Boolean)

        this.previews = sorted;
        if (data?.settings?.length === 0) {
          this.previews = data?.instances;
        }
      }

      setTimeout(async () => {
        this.isLoading = false;
      }, 400);
    },
    dataURLToBlob(dataURL) {
      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mimeString });
    },
    async updateThumbnails(customer_id, layoutId) {
      const { layoutChanges } = getShowPageScope();
      const formData = new FormData();
      const changedPreviews = window.previews.filter(preview => {
        return layoutChanges.find(change => change.instance_id === preview.id);
      });

      for (const preview of changedPreviews) {
        const dataURL = preview.canvas.toDataURL('image/png');
        const blob = this.dataURLToBlob(dataURL);

        // Create a unique filename (optional)
        const filename = `thumb_${preview.id}.png`;

        // Append file and id to FormData
        formData.append('thumbnails[]', new File([blob], filename, { type: 'image/png' }));
        formData.append('instanceIds[]', preview.id);
        
      }
      formData.append('notifyWhenReady', true);
      const url = `/banner/${customer_id}/group/${layoutId}/save_thumbnails`;
      const { data } = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return data.id;
    },
    onImageLoad() {
      this.loadedCount++
      if (this.loadedCount >= this.previews.length) {
        this.isLoading = false
      }
    },
    onModalHidden() {
      this.selectedItems = [];
      this.presetTemplates = []
      this.previews = [];
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedItems = [];
        this.presetTemplates = [];
        return;
      }

      this.selectedItems = this.previews.map(p => p.id);
      this.presetTemplates = this.previews.map(preview => this.mapTemplateToPreset(preview));
    },
    mapTemplateToPreset(template) {
      return {
        ...template,
        original_path: template.thumbnail
      }
    },
  }
}
</script>

<style lang="scss" scoped>

.select-images-modal {
  &__title {
    color: #000000;
    font-weight: bold;
  }
  
  &__actions {
    border-bottom: 1px solid #e9ecef;
    padding-bottom: 8px;
  }
  
  &_selected {
    color: #677489;
  }
  
  &__submit-btn {
    background-color: #59ba9e;
    border: none;

    &:not(:disabled):hover {
      background-color: #007964;
    }
  }
}

.image-card-template {
  display: inline-block;
  margin: 8px;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  background: #eaeaea;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  width: 160px;
  height: 160px;

  &__check {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #59ba9e;
    color: white;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    font-size: 24px;
  }
}
.image-card-template img {
  display: block;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover; 
  vertical-align: middle;
  transition: transform .25s ease;
}

.image-card-template.selected {
  border-color: #59ba9e;
}
.thumbnail-loading {
  width: 7rem;
  height: 7rem;
  margin-top: 60px;
  margin-bottom: 60px;
}
</style>
